import { useState } from "react";

import type { KioskClassRef, KioskLoginSuccess, KioskOpenEntry } from "../types/chipTypes";
import { ClassWeek } from "./ClassWeek";
import { CompactHeader } from "./CompactHeader";
import { OpenEntriesList } from "./OpenEntriesList";

function mondayOf(d: Date): string {
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  const m = new Date(d);
  m.setHours(0, 0, 0, 0);
  m.setDate(m.getDate() - (dow - 1));
  return m.toISOString().slice(0, 10);
}

function shiftWeek(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function GreetScreen({
  session,
  idleRemainingMs,
}: {
  session: KioskLoginSuccess;
  /** Time remaining (ms) until idle auto-logout fires. Null when disabled.
   *  Rendered as a thin progress bar across the top of the screen. */
  idleRemainingMs?: number | null;
}) {
  const name = [session.user.first_name, session.user.last_name].filter(Boolean).join(" ").trim() || "Lehrkraft";
  const myClasses = session.my_classes ?? [];

  // State for drilling into a specific lesson from the fehlende-Stunden list.
  const [drillClass, setDrillClass]   = useState<KioskClassRef | null>(null);
  const [drillWeek, setDrillWeek]     = useState<string | null>(null);
  const [drillLesson, setDrillLesson] = useState<string | null>(null);

  // Determine current lesson: prefer server-detected value, but fall back to a
  // client-side check against today_lessons using Date.now(). The client check
  // lets dev fake-dates work and handles minor server/client clock drift.
  const TOLERANCE_MS = 10 * 60_000;
  const clientCurrentLesson = session.current_lesson ?? (() => {
    const now = Date.now();
    return (session.today_lessons ?? []).find((l) => {
      const start = new Date(l.start_datetime).getTime();
      const end   = new Date(l.end_datetime).getTime();
      return start - TOLERANCE_MS <= now && now < end + TOLERANCE_MS;
    }) ?? null;
  })();

  // If currently teaching → go straight to that class's Klassenbuch.
  const currentClass: KioskClassRef | null = clientCurrentLesson
    ? (myClasses.find((c) => c.id === clientCurrentLesson.class_id)
        ?? { id: clientCurrentLesson.class_id, name: clientCurrentLesson.class_name, is_klassenleiter: false })
    : null;

  const [activeClass, setActiveClass] = useState<KioskClassRef | null>(currentClass);
  const [weekStart, setWeekStart]     = useState<string>(() => mondayOf(new Date()));
  const currentWeekStart = mondayOf(new Date());
  const minWeekStart = shiftWeek(currentWeekStart, -7);
  const maxWeekStart = shiftWeek(currentWeekStart, 7);
  const isClassResponsible = !!activeClass?.is_klassenleiter;
  const canGoPrevWeek = isClassResponsible || weekStart > minWeekStart;
  const canGoNextWeek = isClassResponsible || weekStart < maxWeekStart;

  // Drill into a specific open entry.
  const openEntry = (entry: KioskOpenEntry) => {
    const classRef: KioskClassRef = myClasses.find((c) => c.id === entry.class_id)
      ?? { id: entry.class_id ?? "", name: entry.class_name ?? "?", is_klassenleiter: false };
    const week = mondayOf(new Date(entry.start_at));
    setDrillClass(classRef);
    setDrillWeek(week);
    setDrillLesson(entry.lesson_id);
    setActiveClass(classRef);
    setWeekStart(week);
  };

  const backToList = () => {
    setDrillClass(null);
    setDrillWeek(null);
    setDrillLesson(null);
  };

  // Show ClassWeek when currently teaching OR when drilled into an entry.
  const showWeek = !!currentClass || (!!drillClass && !!drillWeek);

  if (showWeek && activeClass) {
    return (
      <div className="flex-1 flex flex-col px-4 py-3 gap-3 w-full">
        <IdleCountdownBar remainingMs={idleRemainingMs ?? null} />
        <CompactHeader
          name={name}
          session={session}
          classes={currentClass ? myClasses : undefined}
          activeClass={currentClass ? activeClass : undefined}
          onPickClass={currentClass ? setActiveClass : undefined}
          onBack={drillClass ? backToList : undefined}
        />
        <ClassWeek
          activeClass={activeClass}
          weekStart={weekStart}
          userId={session.user.id}
          onPrevWeek={() =>
            setWeekStart((w) => {
              if (!isClassResponsible && w <= minWeekStart) return w;
              return shiftWeek(w, -7);
            })
          }
          onNextWeek={() =>
            setWeekStart((w) => {
              if (!isClassResponsible && w >= maxWeekStart) return w;
              return shiftWeek(w, 7);
            })
          }
          canPrevWeek={canGoPrevWeek}
          canNextWeek={canGoNextWeek}
          targetLessonId={drillLesson ?? undefined}
        />
      </div>
    );
  }

  // No current lesson — show fehlende Stunden list.
  return (
    <div className="flex-1 flex flex-col px-4 py-3 gap-3 w-full">
      <IdleCountdownBar remainingMs={idleRemainingMs ?? null} />
      <header className="flex items-center gap-4">
        <h1 className="text-lg font-light text-slate-50 truncate">{name}</h1>
      </header>

      <OpenEntriesList entries={session.open_entries} userId={session.user.id} onOpenAttendance={openEntry} />
    </div>
  );
}

/**
 * Thin progress bar + mm:ss countdown rendered at the top of the GreetScreen.
 * Only visible during the LAST `COUNTDOWN_VISIBLE_MS` of the idle timeout
 * (currently 60 s); for the rest of the session the bar isn't shown — no
 * point counting from 3:00, that's just noise.
 *
 * Mounting cost is zero when remainingMs is null or > COUNTDOWN_VISIBLE_MS.
 */
const COUNTDOWN_VISIBLE_MS = 60_000;

function IdleCountdownBar({ remainingMs }: { remainingMs: number | null }) {
  if (remainingMs == null) return null;
  // Only show in the last minute of the idle timeout.
  if (remainingMs > COUNTDOWN_VISIBLE_MS) return null;

  // pct measures fill within the visible window (last minute only).
  const pct = Math.max(0, Math.min(100, (remainingMs / COUNTDOWN_VISIBLE_MS) * 100));
  const seconds = Math.ceil(remainingMs / 1000);
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  const label = `${mm}:${ss.toString().padStart(2, "0")}`;

  // Colour tint based on remaining time.
  const isCritical = remainingMs < 10_000;
  const isWarning = remainingMs < 30_000;
  const barColor = isCritical
    ? "bg-rose-500"
    : isWarning
    ? "bg-amber-400"
    : "bg-sky-400";
  const textColor = isCritical
    ? "text-rose-300"
    : isWarning
    ? "text-amber-300"
    : "text-slate-400";

  return (
    <div className="flex items-center gap-2 text-[11px] font-mono select-none">
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-1000 ease-linear`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`${textColor} tabular-nums w-10 text-right`}>{label}</span>
    </div>
  );
}
